'use client';

/**
 * @brisa/ui · components/ui/input — shadcn Input (Facelift v1)
 *
 * Feature: migracao-facelift-v1 · ADR §D6 + COMPONENT_MAP §1 #9
 * Figma origem: 163:936 (input com Icon/Mail) — reforma per 181:493
 *   (Bruno 2026-05-15): bg-white, rounded-[24px], px-24 py-16, leading icon
 *   slot (lucide), trailing slot, error state com border + mensagem.
 *
 * W3.2 admin-basico-login (UI_SPEC v1.3 §2.6 — Figma 223:787):
 *   variant pill-lg adicionado — h-14 (56px), border-[hsl(var(--text-16))]
 *   visível, label externa Archivo SemiBold 13px CAPS tracking-0.05em, e
 *   `showPasswordToggle` quando `type="password"` (Eye/EyeOff lucide).
 *
 * API:
 *   <Input placeholder="Seu E-mail" leadingIcon={<Mail size={16} />} />
 *   <Input error="E-mail inválido" defaultValue="abc" />
 *   <Input trailingIcon={<X size={16} />} onClick={() => clear()} />
 *   // facelift-pill (login Figma 223:787):
 *   <Input
 *     inputSize="lg"
 *     bordered
 *     label="E-mail"
 *     leadingIcon={<Mail size={16} />}
 *     type="email"
 *   />
 *   <Input
 *     inputSize="lg"
 *     bordered
 *     label="Senha"
 *     leadingIcon={<Lock size={16} />}
 *     type="password"
 *     showPasswordToggle
 *   />
 *
 * 'use client' obrigatório (F1-RSC-02) — interatividade de form state +
 * password toggle.
 */
import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { cn } from '../../lib/utils';

export type InputFaceliftSize = 'md' | 'lg';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Ícone à esquerda (typicamente lucide-react). Quando presente, ativa wrapper flex. */
  leadingIcon?: React.ReactNode;
  /** Ícone à direita (typicamente lucide-react). Conflita com showPasswordToggle. */
  trailingIcon?: React.ReactNode;
  /** Mensagem de erro — ativa border-destructive + texto vermelho abaixo. */
  error?: string;
  /** id da mensagem de erro para aria-describedby (gerado automaticamente se omitido). */
  errorId?: string;
  /**
   * Tamanho do input. Default `md` (48px, sem border visível — comportamento legacy).
   * `lg` (56px, border `text-16` visível) per UI_SPEC §2.6 (Figma 223:787 login).
   */
  inputSize?: InputFaceliftSize;
  /**
   * Quando true, aplica border visível `hsl(var(--text-16))` no wrapper. Default
   * `false` (border transparente, comportamento legacy). UI_SPEC §2.6 obriga
   * `bordered` no login facelift v1.3.
   */
  bordered?: boolean;
  /**
   * Label externa renderizada acima do wrapper — Archivo SemiBold 13px CAPS
   * tracking-0.05em `text-64`. UI_SPEC §2.6 obriga para inputs do login.
   * Se omitida, nenhum label é renderizado (consumer pode renderizar via
   * `<Label>` separado).
   */
  label?: string;
  /**
   * Quando true e `type="password"`, renderiza botão trailing com Eye/EyeOff
   * lucide que toggla `type` entre `password` e `text`. Conflita com
   * `trailingIcon` (showPasswordToggle prevalece para type=password).
   */
  showPasswordToggle?: boolean;
  /** ARIA label do botão de toggle (default em PT). */
  togglePasswordShowLabel?: string;
  togglePasswordHideLabel?: string;
}

const WRAPPER_BASE =
  // Pill (rounded-full) per Bruno 2026-05-15 — Input passa a casar com pill-buttons.
  // py-* removido: items-center centraliza vertical sem padding manual.
  'flex w-full items-center gap-2 rounded-full bg-white px-6 ' +
  'transition-colors duration-fast ease-out ' +
  'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2';

const INPUT_BASE =
  // Remove default browser styling (border, outline, background) que rendia o
  // "retângulo dentro do pill". appearance-none zera Safari/iOS chrome.
  'flex-1 min-w-0 appearance-none border-0 bg-transparent text-sm text-[hsl(var(--text-100))] ' +
  'placeholder:text-[hsl(var(--text-40))] ' +
  'focus:outline-none focus-visible:outline-none ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

const SIZE_CLASS: Record<InputFaceliftSize, string> = {
  md: 'h-12',
  lg: 'h-14',
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      leadingIcon,
      trailingIcon,
      error,
      errorId,
      id,
      inputSize = 'md',
      bordered = false,
      label,
      showPasswordToggle = false,
      togglePasswordShowLabel = 'Mostrar senha',
      togglePasswordHideLabel = 'Ocultar senha',
      ...props
    },
    ref,
  ) => {
    const hasError = !!error;
    const generatedErrorId = React.useId();
    const computedErrorId = errorId ?? `${id ?? generatedErrorId}-error`;
    const ariaInvalid = props['aria-invalid'] ?? (hasError ? true : undefined);
    const ariaDescribedBy = hasError
      ? [props['aria-describedby'], computedErrorId].filter(Boolean).join(' ')
      : props['aria-describedby'];

    const generatedInputId = React.useId();
    const resolvedInputId = id ?? generatedInputId;

    const isPasswordToggleable =
      showPasswordToggle && (type === 'password' || type === undefined);
    const [revealed, setRevealed] = React.useState(false);
    const effectiveType = isPasswordToggleable && revealed ? 'text' : (type ?? 'text');

    const borderClass = hasError
      ? 'border-destructive'
      : bordered
        ? 'border-[hsl(var(--text-16))]'
        : 'border-transparent';

    return (
      <div className={cn('flex flex-col gap-2 w-full', className)} data-slot="input-field">
        {label ? (
          <label
            htmlFor={resolvedInputId}
            data-slot="label"
            className="font-body text-[13px] font-semibold uppercase text-[hsl(var(--text-64))]"
            style={{ letterSpacing: '0.05em' }}
          >
            {label}
          </label>
        ) : null}
        <div
          className={cn(WRAPPER_BASE, SIZE_CLASS[inputSize], 'border', borderClass)}
          data-slot="input-wrapper"
        >
          {leadingIcon ? (
            <span
              className="inline-flex shrink-0 items-center justify-center text-[hsl(var(--text-40))]"
              data-slot="leading-icon"
              aria-hidden="true"
            >
              {leadingIcon}
            </span>
          ) : null}
          <input
            ref={ref}
            id={resolvedInputId}
            type={effectiveType}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
            className={INPUT_BASE}
            {...props}
          />
          {isPasswordToggleable ? (
            <button
              type="button"
              data-slot="password-toggle"
              onClick={() => setRevealed((v) => !v)}
              aria-label={revealed ? togglePasswordHideLabel : togglePasswordShowLabel}
              aria-pressed={revealed}
              tabIndex={-1}
              className={cn(
                'inline-flex shrink-0 items-center justify-center rounded-full',
                'text-[hsl(var(--text-40))] hover:text-[hsl(var(--text-100))]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
            >
              {revealed ? (
                <EyeOff size={16} aria-hidden="true" />
              ) : (
                <Eye size={16} aria-hidden="true" />
              )}
            </button>
          ) : trailingIcon ? (
            <span
              className="inline-flex shrink-0 items-center justify-center text-[hsl(var(--text-40))]"
              data-slot="trailing-icon"
              aria-hidden="true"
            >
              {trailingIcon}
            </span>
          ) : null}
        </div>
        {hasError ? (
          <p
            id={computedErrorId}
            data-slot="error-message"
            className="px-2 text-xs font-medium text-destructive"
          >
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';
